import type { IAiAgentRepository } from "@/application/ports/repositories/IAiAgentRepository";
import type {
	AgentFeedbackRequest,
	AgentQueryResponse,
} from "@/domain/aiAgent";
import { apiClient } from "../client";
import { logger } from "@/infrastructure/logger";

export class AiAgentRepository implements IAiAgentRepository {
	private basePath = "/agent";

	async submitFeedback(request: AgentFeedbackRequest): Promise<void> {
		try {
			await apiClient.post(`${this.basePath}/feedback`, request, {
				timeout: 10_000,
			});
		} catch (error) {
			logger.error("Failed to submit AI agent feedback:", error);
			throw error;
		}
	}

	async sendQuery(
		question: string,
		sessionId?: string,
		signal?: AbortSignal,
	): Promise<AgentQueryResponse> {
		signal?.throwIfAborted();

		const responsePromise = apiClient.post<AgentQueryResponse>(
			`${this.basePath}/query`,
			{ question, session_id: sessionId },
			{ timeout: 60_000 },
		);

		if (signal) {
			const abortPromise = new Promise<never>((_, reject) => {
				const onAbort = () => {
					reject(new DOMException("The operation was aborted.", "AbortError"));
				};
				if (signal.aborted) {
					onAbort();
					return;
				}
				signal.addEventListener("abort", onAbort, { once: true });
				responsePromise.finally(() =>
					signal.removeEventListener("abort", onAbort),
				);
			});

			const response = await Promise.race([responsePromise, abortPromise]);
			return response.data;
		}

		const response = await responsePromise;
		return response.data;
	}
}
